import { useRef, useState } from 'react';
import { useMotion } from '@/hooks/useEditorialMotion';
import SubpageShell from '@/components/feature/SubpageShell';
import BookingCta from '@/components/feature/BookingCta';
import { serviceGroups, serviceTiers } from '@/mocks/home';

export default function ServicesPage() {
  const [tier, setTier] = useState(0);
  const ref = useRef<HTMLElement>(null);
  useMotion(ref);
  return (
    <SubpageShell title="Services & Pricing" description="Explore our cutting, colour and care services, tailored to your individual style.">
      <div className="sticky top-16 md:top-20 z-30 bg-background-50/95 backdrop-blur border-y border-background-300">
        <div className="max-w-[1320px] mx-auto px-5 md:px-12 lg:px-16 flex flex-wrap items-center justify-between gap-x-8 gap-y-3 py-4">
          <nav className="flex flex-wrap gap-x-7 gap-y-2" aria-label="Service categories">
            {serviceGroups.map((g) => (
              <a key={g.id} href={`#${g.id}`} className="text-[14px] font-medium text-foreground-700 hover:text-foreground-950 whitespace-nowrap cursor-pointer">{g.title}</a>
            ))}
          </nav>
          <div className="inline-flex p-1 rounded-full bg-background-100 flex-wrap">
            {serviceTiers.map((t, i) => (
              <button key={t} type="button" onClick={() => setTier(i)} className={`px-4 py-1.5 rounded-full text-[13px] whitespace-nowrap cursor-pointer transition-colors ${tier === i ? 'bg-foreground-950 text-background-100' : 'text-foreground-700 hover:text-foreground-950'}`}>{t}</button>
            ))}
          </div>
        </div>
      </div>

      <section ref={ref} className="bg-background-50">
        <div className="max-w-[1320px] mx-auto px-5 md:px-12 lg:px-16">
          {serviceGroups.map((g) => (
            <div key={g.id} id={g.id} data-group className="scroll-mt-40 grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-6 py-14 md:py-20 border-b border-background-300 last:border-b-0">
              <div className="lg:col-span-4">
                <h2 data-m="left" className="font-heading font-semibold tracking-[-0.02em] text-[28px] md:text-[40px] leading-[1.1] text-foreground-950">{g.title}</h2>
                <p data-m="sub" data-at="0.12" className="mt-4 text-[15px] leading-relaxed text-foreground-700 max-w-xs">{g.note}</p>
              </div>
              <div className="lg:col-span-8">
                <div className="hidden md:grid grid-cols-[1fr_96px_72px] gap-x-6 pb-3 text-[13px] text-foreground-600">
                  <span>Service</span><span>Duration</span><span className="text-right">{serviceTiers[tier]}</span>
                </div>
                <span data-m="line" data-at="0.15" aria-hidden="true" className="block h-px bg-foreground-950"></span>
                <ul>
                  {g.items.map((s) => (
                    <li key={s.name} data-m="row" data-at="0.3" className="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_96px_72px] gap-x-6 items-center min-h-[64px] py-4 border-b border-background-300">
                      <div className="min-w-0">
                        <p className="text-[16px] font-semibold text-foreground-950">{s.name}</p>
                        <p className="mt-0.5 text-[14px] text-foreground-600">{s.desc}<span className="md:hidden"> · {s.time}</span></p>
                      </div>
                      <p className="hidden md:block text-[14px] text-foreground-600 whitespace-nowrap">{s.time}</p>
                      <p className="text-[16px] font-semibold text-foreground-950 text-right whitespace-nowrap">{s.prices[tier]}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
          <p className="pb-16 md:pb-24 text-[13px] text-foreground-600">Prices are placeholders. Final price depends on hair length and is confirmed at consultation.</p>
        </div>
      </section>

      <BookingCta title="Ready to book?" text="Choose your service and stylist when you book. New clients start with a short consultation." />
    </SubpageShell>
  );
}